import React, { useEffect, useState } from 'react';
import DashboardCard from '../components/DashboardCard';
import { fetchOrders, fetchProducts } from '../utils/api';
import { Line, Bar } from 'react-chartjs-2';
import 'chart.js/auto';

const Dashboard = () => {
    const [totalSales, setTotalSales] = useState(0);
    const [totalOrders, setTotalOrders] = useState(0);
    const [totalCustomers, setTotalCustomers] = useState(0);
    const [totalProducts, setTotalProducts] = useState(0);
    const [lastOrders, setLastOrders] = useState([]);
    const [bestSellingProducts, setBestSellingProducts] = useState([]);
    const [salesData, setSalesData] = useState({});
    const [productSalesData, setProductSalesData] = useState({});

    
    useEffect(() => {
        getDashboardStats();
    }, []);
    
    const getDashboardStats = async () => {
        try {
            const orders = await fetchOrders();
            const products = await fetchProducts();

            const validOrders = orders.filter(order => order.items && order.items.length > 0);


            const uniqueCustomers = new Set();
            let sales = 0;
            const salesByDate = {};
            const productSales = {};

            validOrders.forEach(order => {
                sales += order.totalAmount;
                uniqueCustomers.add(order.customerPhone);
            
                const orderDate = new Date(order.createdAt).toLocaleDateString();
                salesByDate[orderDate] = (salesByDate[orderDate] || 0) + order.totalAmount;
            
                order.items.forEach(item => {
                    if (item.productId && item.productId.name) {
                        productSales[item.productId.name] = (productSales[item.productId.name] || 0) + item.quantity;
                    }
                });
            });

            const sortedProductSales = Object.entries(productSales)
                .filter(([name]) => name)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
            ;

            setTotalSales(sales);
            setTotalOrders(orders.length);
            setTotalCustomers(uniqueCustomers.size);
            setTotalProducts(products.length);
            setLastOrders(orders.slice(-5).reverse());
            setBestSellingProducts(sortedProductSales);

            setSalesData({
                labels: Object.keys(salesByDate),
                datasets: [{
                    label: 'Salg over tid (NOK)',
                    data: Object.values(salesByDate),
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    borderColor: 'rgba(75, 192, 192, 1)',
                    borderWidth: 2
                }]
            });

            setProductSalesData({
                labels: sortedProductSales.map(([name]) => name),
                datasets: [{
                    label: 'Antall Solgt',
                    data: sortedProductSales.map(([, quantity]) => quantity),
                    backgroundColor: 'rgba(255, 99, 132, 0.6)'
                }]
            });

        } catch (error) {
            console.error("Feil ved henting av dashboard-statistikk:", error);
        }
    };

    const stats = [
        { title: 'Totalt Salg', value: `${totalSales.toLocaleString()} NOK`, color: 'bg-blue-500' },
        { title: 'Totalt Ordre', value: totalOrders, color: 'bg-green-500' },
        { title: 'Totalt Kunder', value: totalCustomers, color: 'bg-purple-500' },
        { title: 'Totalt Produkter', value: totalProducts, color: 'bg-yellow-500' },
    ];

    const getStatus = (status) => {
        if (status === "pending") {
            return <span className="text-orange-500 font-semibold">Venter</span>;
        } else if (status === "completed") {
            return <span className="text-green-500 font-semibold">Betalt</span>;
        } else {
            return <span className="text-blue-500 font-semibold">Fullført</span>;
        }
    };

    return (
        <div className="p-4">
            <h1 className="text-2xl font-bold mb-6">Dashboard</h1>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                    <DashboardCard key={index} {...stat} />
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="bg-white p-4 rounded-lg shadow-md">
                    <h2 className="text-lg font-semibold mb-3">Salg over tid</h2>
                    {salesData.datasets && <Line data={salesData} />}
                </div>

                <div className="bg-white p-4 rounded-lg shadow-md">
                    <h2 className="text-lg font-semibold mb-3">Bestselgende produkter</h2>
                    {productSalesData.datasets && <Bar data={productSalesData} />}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                <div className="bg-white p-4 rounded-lg shadow-md md:col-span-2">
                    <h2 className="text-lg font-semibold mb-3">Siste 5 ordrer</h2>
                    <table className="w-full border-collapse text-sm">
                        <thead className="bg-gray-200">
                            <tr>
                                <th className="p-2 text-left">Navn</th>
                                <th className="p-2 text-left">Total</th>
                                <th className="p-2 text-left">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lastOrders.map(order => (
                                <tr key={order._id} className="border-t">
                                    <td className="p-2">{order.customerName}</td>
                                    <td className="p-2">{order.totalAmount} NOK</td>
                                    <td className={"p-2 font-semibold"}>
                                        {getStatus(order.status)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                
                <div className="bg-white p-4 rounded-lg shadow-md">
                    <h2 className="text-lg font-semibold mb-3">Bestselgende produkter</h2>
                    <table className="w-full border-collapse text-sm">
                        <thead className="bg-gray-200">
                            <tr>
                                <th className="p-2 text-left">Produkt</th>
                                <th className="p-2 text-left">Antall solgt</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bestSellingProducts.map(([name, quantity]) => (
                                <tr key={name} className="border-t">
                                    <td className="p-2">{name}</td>
                                    <td className="p-2 font-bold">{quantity}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
